import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Badge,
  Grid,
  FormControl,
  InputLabel,
  useTheme,
  alpha,
  Pagination,
  Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PendingActions as PendingIcon
} from '@mui/icons-material';
import { getWorkflowTasks, updateWorkflowTask } from '../../services/encounterService';
import { auth } from '../../firebase/config';
import { UserRole, RoleUtils } from '../../types/enums';
import { useUserData } from '../../hooks/useUserData';

// Enhanced StatusChip component with better visual design
const StatusChip = ({ status }) => {
  const theme = useTheme();
  
  const statusConfig = {
    pending: { 
      color: 'default', 
      icon: <PendingIcon fontSize="small" />, 
      label: 'Pending',
      bgColor: alpha(theme.palette.grey[500], 0.1)
    },
    in_progress: { 
      color: 'primary', 
      icon: <AssignmentIcon fontSize="small" />, 
      label: 'In Progress',
      bgColor: alpha(theme.palette.primary.main, 0.1)
    },
    completed: { 
      color: 'success', 
      icon: <CheckCircleIcon fontSize="small" />, 
      label: 'Completed',
      bgColor: alpha(theme.palette.success.main, 0.1)
    },
    overdue: { 
      color: 'error', 
      icon: <ErrorIcon fontSize="small" />, 
      label: 'Overdue',
      bgColor: alpha(theme.palette.error.main, 0.1)
    },
    skipped: { 
      color: 'default', 
      icon: <ScheduleIcon fontSize="small" />, 
      label: 'Skipped',
      bgColor: alpha(theme.palette.grey[500], 0.1)
    },
    cancelled: { 
      color: 'default', 
      icon: <ScheduleIcon fontSize="small" />, 
      label: 'Cancelled',
      bgColor: alpha(theme.palette.grey[500], 0.1)
    }
  };

  const config = statusConfig[status] || statusConfig.pending;
  
  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size="small"
      sx={{
        minWidth: 110,
        fontWeight: 600,
        backgroundColor: config.bgColor,
        '& .MuiChip-icon': {
          fontSize: '16px'
        }
      }}
    />
  );
};

// Helper function to calculate due date based on timing metadata
function calculateDueDateFromTiming(admissionTime, stepMetadata) {
  if (!stepMetadata || !stepMetadata.timingType || !admissionTime) return null;
  
  const baseTime = new Date(admissionTime).getTime();
  
  switch (stepMetadata.timingType) {
    case 'at_admission':
      return new Date(baseTime);
    case 'minutes':
      return stepMetadata.timingValue ? new Date(baseTime + stepMetadata.timingValue * 60 * 1000) : null;
    case 'hours':
    case 'by_x_hours':
      return stepMetadata.timingValue ? new Date(baseTime + stepMetadata.timingValue * 60 * 60 * 1000) : null;
    case 'days':
      return stepMetadata.timingValue ? new Date(baseTime + stepMetadata.timingValue * 24 * 60 * 60 * 1000) : null;
    case 'weeks':
      return stepMetadata.timingValue ? new Date(baseTime + stepMetadata.timingValue * 7 * 24 * 60 * 60 * 1000) : null;
    case 'months':
      if (stepMetadata.timingValue) {
        const dueDate = new Date(admissionTime);
        dueDate.setMonth(dueDate.getMonth() + stepMetadata.timingValue);
        return dueDate;
      }
      return null;
    case 'daily':
      const nextDay = new Date(admissionTime);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(23, 59, 59, 999);
      return nextDay;
    case 'per_shift':
      const hoursIntoShift = new Date(admissionTime).getHours() % 8;
      const hoursToEndOfShift = 8 - hoursIntoShift;
      return new Date(baseTime + hoursToEndOfShift * 60 * 60 * 1000);
    case 'every_x_hours':
      return stepMetadata.timingValue ? new Date(baseTime + stepMetadata.timingValue * 60 * 60 * 1000) : null;
    case 'as_soon_as_approved':
      return new Date(baseTime + 60 * 60 * 1000);
    case 'ongoing':
      return null;
    default:
      if (stepMetadata.timingValue && typeof stepMetadata.timingValue === 'number') {
        return new Date(baseTime + stepMetadata.timingValue * 60 * 60 * 1000);
      }
      return null;
  }
}

// Helper function to safely parse and format due date
function formatDueDate(task, encounter) {
  let dueDate = null;
  
  if (task.dueAt) {
    // Handle Firestore Timestamp objects
    if (task.dueAt._seconds !== undefined && task.dueAt._nanoseconds !== undefined) {
      dueDate = new Date(task.dueAt._seconds * 1000 + task.dueAt._nanoseconds / 1000000);
    } else if (task.dueAt.seconds) {
      dueDate = new Date(task.dueAt.seconds * 1000);
    } else if (task.dueAt.toDate) {
      dueDate = task.dueAt.toDate();
    } else {
      dueDate = new Date(task.dueAt);
    }
  }
  
  if (!dueDate || isNaN(dueDate.getTime())) {
    const admissionTime = encounter?.startDate || encounter?.createdAt;
    if (admissionTime && task.stepMetadata) {
      // Handle Firestore Timestamp objects in admissionTime
      let baseTime;
      if (admissionTime._seconds !== undefined && admissionTime._nanoseconds !== undefined) {
        baseTime = new Date(admissionTime._seconds * 1000 + admissionTime._nanoseconds / 1000000);
      } else if (admissionTime.seconds) {
        baseTime = new Date(admissionTime.seconds * 1000);
      } else if (admissionTime.toDate) {
        baseTime = admissionTime.toDate();
      } else {
        baseTime = new Date(admissionTime);
      }
      
      if (!isNaN(baseTime.getTime())) {
        dueDate = calculateDueDateFromTiming(baseTime, task.stepMetadata);
      }
    }
  }
  
  // If we still don't have a valid due date and encounter data is not available,
  // try to use the current time as a fallback for timing calculations
  if ((!dueDate || isNaN(dueDate.getTime())) && !encounter && task.stepMetadata) {
    const currentTime = new Date();
    dueDate = calculateDueDateFromTiming(currentTime, task.stepMetadata);
  }
  
  if (dueDate && !isNaN(dueDate.getTime())) {
    return dueDate.toLocaleString();
  }
  
  return 'No due date';
}

// Compact task row component
const TaskRow = ({ task, encounter, staffList, onAssign, onStatusChange, onNotesChange, updating }) => {
  const [localNotes, setLocalNotes] = useState(task.notes || '');
  const [notesTimeout, setNotesTimeout] = useState(null);

  // Debounced notes update
  const handleNotesChange = useCallback((newNotes) => {
    setLocalNotes(newNotes);
    if (notesTimeout) clearTimeout(notesTimeout);
    setNotesTimeout(setTimeout(() => onNotesChange(task.stepId, newNotes), 1000));
  }, [task.stepId, onNotesChange, notesTimeout]);

  const isOverdue = task.status === 'overdue';
  
  return (
    <TableRow
      sx={{
        backgroundColor: isOverdue ? alpha('#d32f2f', 0.05) : 'transparent',
        '&:hover': {
          backgroundColor: alpha('#1976d2', 0.04)
        },
        height: 48
      }}
    >
      <TableCell sx={{ py: 0.5, fontSize: '0.875rem', fontWeight: 500 }}>
        {task.stepMetadata?.title || 'Unknown Task'}
      </TableCell>
      <TableCell sx={{ py: 0.5, minWidth: 140 }}>
        <FormControl size="small" fullWidth>
          <Select
            value={task.assignedTo || ''}
            onChange={(e) => onAssign(task.stepId, e.target.value)}
            disabled={updating === task.stepId}
            displayEmpty
            sx={{ fontSize: '0.875rem' }}
          >
            <MenuItem value="">Unassigned</MenuItem>
            {staffList.map(staff => (
              <MenuItem key={staff.id} value={staff.id}>{staff.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell sx={{ py: 0.5 }}>
        <StatusChip status={task.status} />
      </TableCell>
      <TableCell sx={{ py: 0.5, fontSize: '0.875rem' }}>
        {formatDueDate(task, encounter)}
      </TableCell>
      <TableCell sx={{ py: 0.5, minWidth: 150 }}>
        <TextField
          size="small"
          value={localNotes}
          onChange={(e) => handleNotesChange(e.target.value)}
          disabled={updating === task.stepId}
          placeholder="Add notes..."
          sx={{ 
            '& .MuiInputBase-input': { 
              fontSize: '0.875rem',
              py: 0.5
            }
          }}
        />
      </TableCell>
      <TableCell sx={{ py: 0.5, minWidth: 130 }}>
        <FormControl size="small" fullWidth>
          <Select
            value={task.status}
            onChange={(e) => onStatusChange(task.stepId, e.target.value)}
            disabled={updating === task.stepId}
            sx={{ fontSize: '0.875rem' }}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="skipped">Skipped</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
    </TableRow>
  );
};

const WorkflowTaskList = ({ encounterId }) => {
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  const { user } = useUserData();
  const allStaffList = useSelector(state => state.facilities.facilityUsers);
  const selectedEncounter = useSelector(state => state.encounters?.selectedEncounter);

  const encounter = selectedEncounter?.id === encounterId ? selectedEncounter : null;

  // Filter staff list to only show specific roles allowed for task assignment
  const allowedRoles = useMemo(() => [
    UserRole.ATTENDING_PHYSICIAN,
    UserRole.NURSE,
    UserRole.CASE_MANAGER,
    UserRole.PHARMACIST,
    UserRole.UNIT_CLERK,
    UserRole.BED_MANAGER,
    UserRole.FACILITY_ADMIN
  ], []);

  const staffList = useMemo(() => {
    const staffListData = allStaffList || [];
    return staffListData.filter(staff => {
      // Check if staff has any of the allowed roles
      const userRoles = staff.roles || [staff.role].filter(Boolean);
      return RoleUtils.hasAnyRole(userRoles, allowedRoles);
    });
  }, [allStaffList, allowedRoles]);

  // Memoized task filtering and pagination
  const { completedTasks, taskCounts, paginatedActiveTasks, paginatedCompletedTasks } = useMemo(() => {
    const active = allTasks.filter(task => 
      task.status === 'pending' || 
      task.status === 'in_progress' || 
      task.status === 'overdue'
    );
    const completed = allTasks.filter(task => 
      task.status === 'completed' || 
      task.status === 'skipped' || 
      task.status === 'cancelled'
    );
    
    // Client-side pagination
    const activeStartIndex = (activePage - 1) * pageSize;
    const activeEndIndex = activeStartIndex + pageSize;
    const completedStartIndex = (completedPage - 1) * pageSize;
    const completedEndIndex = completedStartIndex + pageSize;
    
    const paginatedActive = active.slice(activeStartIndex, activeEndIndex);
    const paginatedCompletedTasks = completed.slice(completedStartIndex, completedEndIndex);
    
    const counts = {
      active: active.length,
      completed: completed.length,
      overdue: allTasks.filter(t => t.status === 'overdue').length,
      inProgress: allTasks.filter(t => t.status === 'in_progress').length,
      totalActive: active.length,
      totalCompleted: completed.length
    };

    return { 
      completedTasks: completed, 
      taskCounts: counts,
      paginatedActiveTasks: paginatedActive,
      paginatedCompletedTasks: paginatedCompletedTasks
    };
  }, [allTasks, activePage, completedPage, pageSize]);

  const fetchTasks = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }
      
      const token = await auth.currentUser.getIdToken();
      
      // For now, fetch all tasks without pagination since backend doesn't support it yet
      const response = await getWorkflowTasks(encounterId, token);
      
      // Handle both paginated and non-paginated responses
      if (response.data && Array.isArray(response.data)) {
        // Paginated response
        setAllTasks(response.data);
      } else if (Array.isArray(response)) {
        // Non-paginated response (current backend behavior)
        setAllTasks(response);
      } else {
        setAllTasks([]);
      }
      
      setError('');
    } catch (e) {
      console.error('Error fetching workflow tasks:', e);
      setError('Failed to load workflow tasks');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [encounterId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleAssign = useCallback(async (stepId, assignedTo) => {
    setUpdating(stepId);
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }
      
      const token = await auth.currentUser.getIdToken();
      await updateWorkflowTask(encounterId, stepId, { assignedTo, status: 'in_progress' }, user?.uid, token);
      
      // Optimistic update instead of full refetch
      setAllTasks(prev => prev.map(task => 
        task.stepId === stepId 
          ? { ...task, assignedTo, status: 'in_progress' }
          : task
      ));
    } catch (e) {
      console.error('Error updating workflow task assignment:', e);
      setError('Failed to update task assignment');
    }
    setUpdating('');
  }, [encounterId, user?.uid]);

  const handleStatusChange = useCallback(async (stepId, status) => {
    setUpdating(stepId);
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }
      
      const token = await auth.currentUser.getIdToken();
      await updateWorkflowTask(encounterId, stepId, { status }, user?.uid, token);
      
      // Optimistic update
      setAllTasks(prev => prev.map(task => 
        task.stepId === stepId 
          ? { ...task, status }
          : task
      ));
    } catch (e) {
      console.error('Error updating workflow task status:', e);
      setError('Failed to update task status');
    }
    setUpdating('');
  }, [encounterId, user?.uid]);

  const handleNotesChange = useCallback(async (stepId, notes) => {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }
      
      const token = await auth.currentUser.getIdToken();
      await updateWorkflowTask(encounterId, stepId, { notes }, user?.uid, token);
      
      // Optimistic update
      setAllTasks(prev => prev.map(task => 
        task.stepId === stepId 
          ? { ...task, notes }
          : task
      ));
    } catch (e) {
      console.error('Error updating workflow task notes:', e);
      setError('Failed to update task notes');
    }
  }, [encounterId, user?.uid]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleActivePageChange = (event, newPage) => {
    setActivePage(newPage);
  };

  const handleCompletedPageChange = (event, newPage) => {
    setCompletedPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    
    // Reset pages to 1 when page size changes to avoid being on non-existent pages
    setActivePage(1);
    setCompletedPage(1);
  };

  // Effect to handle page bounds when task counts change
  useEffect(() => {
    const maxActivePage = Math.max(1, Math.ceil(taskCounts.totalActive / pageSize));
    const maxCompletedPage = Math.max(1, Math.ceil(taskCounts.totalCompleted / pageSize));
    
    if (activePage > maxActivePage) {
      setActivePage(maxActivePage);
    }
    
    if (completedPage > maxCompletedPage) {
      setCompletedPage(maxCompletedPage);
    }
  }, [taskCounts.totalActive, taskCounts.totalCompleted, pageSize, activePage, completedPage]);

  const renderTaskTable = (taskList) => (
    <TableContainer sx={{ maxHeight: 400 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, py: 1 }}>Task</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 1 }}>Assigned To</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 1 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 1 }}>Due Date</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 1 }}>Notes</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 1 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {taskList.map(task => (
            <TaskRow
              key={task.stepId}
              task={task}
              encounter={encounter}
              staffList={staffList}
              onAssign={handleAssign}
              onStatusChange={handleStatusChange}
              onNotesChange={handleNotesChange}
              updating={updating}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" sx={{ ml: 2 }}>Loading workflow tasks...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <IconButton onClick={() => fetchTasks()} sx={{ ml: 1 }}>
          <RefreshIcon />
        </IconButton>
      </Alert>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 2 }}>
      {/* Header with stats and refresh */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Grid container spacing={1}>
              <Grid item>
                <Chip 
                  label={`${taskCounts.active} Active`} 
                  color="primary" 
                  size="small"
                  variant="outlined"
                />
              </Grid>
              <Grid item>
                <Chip 
                  label={`${taskCounts.completed} Completed`} 
                  color="success" 
                  size="small"
                  variant="outlined"
                />
              </Grid>
              {taskCounts.overdue > 0 && (
                <Grid item>
                  <Chip 
                    label={`${taskCounts.overdue} Overdue`} 
                    color="error" 
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item>
            <Tooltip title="Refresh tasks">
              <IconButton 
                onClick={() => fetchTasks(true)} 
                disabled={refreshing}
                size="small"
              >
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ minHeight: 40 }}
        >
          <Tab 
            label={
              <Badge 
                badgeContent={taskCounts.active} 
                color="primary"
                invisible={taskCounts.active === 0}
              >
                Active Tasks
              </Badge>
            }
            sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
          />
          <Tab 
            label={
              <Badge 
                badgeContent={taskCounts.completed} 
                color="default"
                invisible={taskCounts.completed === 0}
              >
                Completed Tasks
              </Badge>
            }
            sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ p: 0 }}>
        {activeTab === 0 && (
          <>
            {paginatedActiveTasks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {taskCounts.totalActive === 0 ? 'No active tasks' : 'No tasks on this page'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {taskCounts.totalActive === 0 
                    ? 'All workflow tasks are completed or pending assignment.'
                    : 'Try navigating to a different page or adjusting the page size.'
                  }
                </Typography>
              </Box>
            ) : (
              <>
                {renderTaskTable(paginatedActiveTasks)}
                <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <Pagination
                    count={Math.ceil(taskCounts.totalActive / pageSize)}
                    page={activePage}
                    onChange={handleActivePageChange}
                    color="primary"
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {Math.min((activePage - 1) * pageSize + 1, taskCounts.totalActive)}-{Math.min(activePage * pageSize, taskCounts.totalActive)} of {taskCounts.totalActive} active tasks
                    </Typography>
                    <FormControl size="small">
                      <InputLabel id="page-size-label">Page Size</InputLabel>
                      <Select
                        labelId="page-size-label"
                        value={pageSize.toString()}
                        onChange={handlePageSizeChange}
                        label="Page Size"
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Stack>
              </>
            )}
          </>
        )}
        
        {activeTab === 1 && (
          <>
            {paginatedCompletedTasks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PendingIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {taskCounts.totalCompleted === 0 ? 'No completed tasks' : 'No tasks on this page'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {taskCounts.totalCompleted === 0 
                    ? 'Completed tasks will appear here.'
                    : 'Try navigating to a different page or adjusting the page size.'
                  }
                </Typography>
              </Box>
            ) : (
              <>
                {renderTaskTable(paginatedCompletedTasks)}
                <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <Pagination
                    count={Math.ceil(taskCounts.totalCompleted / pageSize)}
                    page={completedPage}
                    onChange={handleCompletedPageChange}
                    color="primary"
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {Math.min((completedPage - 1) * pageSize + 1, taskCounts.totalCompleted)}-{Math.min(completedPage * pageSize, taskCounts.totalCompleted)} of {taskCounts.totalCompleted} completed tasks
                    </Typography>
                    <FormControl size="small">
                      <InputLabel id="page-size-label-completed">Page Size</InputLabel>
                      <Select
                        labelId="page-size-label-completed"
                        value={pageSize.toString()}
                        onChange={handlePageSizeChange}
                        label="Page Size"
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Stack>
              </>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default WorkflowTaskList; 